import React, { useState } from "react";
import { FileUpload } from "../UI/FileUpload";

interface Review {
  id: number;
  name?: string;
  title?: string;
  quote?: string;
  image?: File | string | null;
  imageId?: string;
  imageName?: string;
  stars?: number;
  imageUrl?: string;
}

interface ReviewsProps {
  formData: {
    reviews?: Review[];
    isEditing?: boolean;
  };
  onUpdate: (field: string, value: any) => void;
  errors: { [key: string]: string };
}

export const Reviews: React.FC<ReviewsProps> = ({
  formData,
  onUpdate,
  errors,
}) => {
  const [reviewCount, setReviewCount] = useState(formData.reviews?.length || 0);

  const handleReviewChange = (
    reviewId: number,
    field: keyof Review,
    value: any,
  ) => {
    const currentReviews = formData.reviews || [];
    const updatedReviews = currentReviews.map((review) =>
      review.id === reviewId ? { ...review, [field]: value } : review,
    );
    onUpdate("reviews", updatedReviews);
  };

  const addReview = () => {
    const newReviewId = reviewCount + 1;
    const newReview: Review = {
      id: newReviewId,
      name: "",
      title: "",
      quote: "",
      imageId: `reviewer${newReviewId}Image`,
      imageName: `reviewer${newReviewId}Image`,
      stars: 3,
    };

    const currentReviews = formData.reviews || [];
    const updatedReviews = [...currentReviews, newReview];
    onUpdate("reviews", updatedReviews);
    setReviewCount(newReviewId);
  };

  const removeReview = (reviewId: number) => {
    const currentReviews = formData.reviews || [];
    const updatedReviews = currentReviews.filter(
      (review) => review.id !== reviewId,
    );
    onUpdate("reviews", updatedReviews);
  };

  const handleImageSelect = (reviewId: number, file: File | null) => {
    handleReviewChange(reviewId, "image", file);
  };

  return (
    <div className="form-step active">
      <p className="text-center text-gray-600 mb-8">
        Add customer testimonials to build credibility (optional)
      </p>

      <div id="reviewsContainer">
        {formData.reviews?.map((review) => (
          <div
            key={review.id}
            className="dynamic-section"
            data-review-id={review.id}
          >
            <button
              type="button"
              className="remove-section"
              onClick={() => removeReview(review.id)}
              title="Remove Review"
            >
              ×
            </button>
            <h3>Customer Review {review.id}</h3>
            <div className="grid-2">
              <div className="form-field">
                <label htmlFor={`reviewer${review.id}`}>Reviewer's name</label>
                <input
                  type="text"
                  id={`reviewer${review.id}`}
                  value={review.name || ""}
                  onChange={(e) =>
                    handleReviewChange(review.id, "name", e.target.value)
                  }
                  onBlur={(e) =>
                    handleReviewChange(review.id, "name", e.target.value.trim())
                  }
                  placeholder="Reviewer's name"
                />
              </div>
              <div className="form-field">
                <label htmlFor={`reviewerTitle${review.id}`}>
                  Reviewer's title
                </label>
                <input
                  type="text"
                  id={`reviewerTitle${review.id}`}
                  value={review.title || ""}
                  onChange={(e) =>
                    handleReviewChange(review.id, "title", e.target.value)
                  }
                  onBlur={(e) =>
                    handleReviewChange(
                      review.id,
                      "title",
                      e.target.value.trim(),
                    )
                  }
                  placeholder="e.g., CEO at Company"
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor={`reviewer${review.id}Image`}>
                Reviewer's image (optional)
              </label>
              <FileUpload
                label=""
                onFileSelect={(file) => handleImageSelect(review.id, file)}
                currentFile={
                  (formData.isEditing && typeof review.image === "string") ? (
                  review.image.startsWith("https://")
                    ? review.image
                    : review.imageUrl) : review.image
                }
                accept={["image/jpeg", "image/jpg", "image/png"]}
                maxSize={5}
                required={false}
                error={errors[`reviews.${review.id}.image`]}
              />
            </div>

            <div className="form-field">
              <label htmlFor={`quote${review.id}`}>Review text</label>
              <textarea
                id={`quote${review.id}`}
                value={review.quote || ""}
                onChange={(e) =>
                  handleReviewChange(review.id, "quote", e.target.value)
                }
                onBlur={(e) =>
                  handleReviewChange(review.id, "quote", e.target.value.trim())
                }
                placeholder="Enter the review or testimonial..."
              />
            </div>

            <div className="form-field">
              <label htmlFor={`stars${review.id}`}>Stars (1-5)</label>
              <input
                type="number"
                id={`stars${review.id}`}
                min={0}
                max={5}
                value={review.stars ?? 0}
                onChange={(e) =>
                  handleReviewChange(
                    review.id,
                    "stars",
                    Math.max(0, Math.min(5, Number(e.target.value) || 0)),
                  )
                }
              />
            </div>
          </div>
        ))}
      </div>

      <button type="button" className="add-section" onClick={addReview}>
        + Add Review
      </button>
    </div>
  );
};
